import { AnimatePresence } from 'framer-motion';
import { DateTime } from 'luxon';
import { FadeInOut } from '@/components/FadeInOut';
import { getDisplayEmoji } from '@/features/events/lib/eventEmoji.lib';
import type { PageExperience } from '@/features/experiences/types';
import { useActiveDate } from '@/features/planner/hooks/useActiveDate';
import { getStyleById } from '@/features/printables/theme';
import { getEventSourceColor } from '@/features/sources/lib/feed.lib';
import { ExperienceColorMode } from '@/types/gql.generated';
import { Box, Button, Flex, Text } from '@/ui';
import { toCompactTimingLabel } from '../../shared/compactTiming.lib';
import { FeedIndicator } from '../../shared/FeedIndicator';
import type { MonthData, GroupData } from '../types';

type Props = {
  monthData: MonthData;
  experience: PageExperience;
};

type EventRowProps = {
  date: DateTime;
  event: GroupData['events'][number];
  experience: PageExperience;
  theme: ReturnType<typeof getStyleById>;
};

const EventRow = ({ date, event, experience, theme }: EventRowProps) => {
  // A source's default emoji outranks the event's own
  // (getDisplayEmoji); only a per-event override beats it.
  const emoji = experience.eventCardEmojiEnabled
    ? getDisplayEmoji(experience, event)
    : null;
  // The feed indicator dot renders small, so force the bold (500) color variant
  // for contrast, regardless of the experience colorMode.
  const indicatorColor = getEventSourceColor(
    experience,
    event,
    ExperienceColorMode.Bold
  );
  const timingLabel = experience.eventCardDateEnabled
    ? toCompactTimingLabel({
        date,
        event,
        timeFormat: experience.timeFormat,
        includeEndTime: experience.eventCardEndTimeEnabled,
      })
    : null;

  return (
    <Flex
      color={theme.neutral3 || theme.textColor}
      flexDirection="column"
      lineHeight="120%"
    >
      {timingLabel && (
        <Text as="span" color={theme.neutral5} fontSize="0.85em" mb="0.1em">
          {timingLabel}
        </Text>
      )}
      <Text alignItems="baseline" as="span" display="inline-flex" gap="0.4em">
        {indicatorColor && (
          <Text
            as="span"
            color={indicatorColor}
            flexShrink={0}
            fontSize="0.7em"
          >
            <FeedIndicator />
          </Text>
        )}
        <Box as="span">
          <Text as="span" fontWeight="500">
            {emoji && (
              <Text as="span" mr="0.25em">
                {emoji}
              </Text>
            )}
            {event.title}
          </Text>
          {experience.eventCardLocationEnabled &&
            event.locationWithPlace?.name && (
              <Text as="span" color={theme.neutral5} fontSize="0.85em">
                {', '}
                {event.locationWithPlace.name}
              </Text>
            )}
        </Box>
      </Text>
    </Flex>
  );
};

export const MobileDayDetails = ({ monthData, experience }: Props) => {
  const [activeDate, setActiveDate] = useActiveDate();
  const theme = getStyleById(experience.styleId ?? undefined);

  const monthStart = DateTime.fromObject({
    year: monthData.month.year,
    month: monthData.month.month,
  }).startOf('month');
  const monthEnd = monthStart.endOf('month');
  const isInMonth = (date: DateTime) => date >= monthStart && date <= monthEnd;

  const activeDateForMonth =
    activeDate && isInMonth(activeDate) ? activeDate : null;

  if (activeDateForMonth) {
    const box: GroupData | undefined = monthData.boxes.find((b) =>
      b.date.hasSame(activeDateForMonth, 'day')
    );
    const events = box?.events ?? [];
    const formattedDate = activeDateForMonth.toFormat('cccc, LLLL d, yyyy');

    return (
      <Box
        borderTopColor={theme.accent2Color}
        borderTopStyle="solid"
        borderTopWidth="1px"
        mt="1.5em"
        pt="1em"
        px="0.5em"
      >
        <AnimatePresence mode="wait">
          <FadeInOut direction="column" key={String(activeDateForMonth)}>
            <Flex
              alignItems="baseline"
              justifyContent="space-between"
              mb="0.5em"
            >
              <Text color={theme.textColor} fontSize="1.1em" fontWeight="600">
                {formattedDate}
              </Text>
              <Button
                color={theme.neutral3 || theme.textColor}
                fontSize="0.85em"
                fontWeight="500"
                size="xs"
                variant="link"
                onClick={() => setActiveDate(null)}
              >
                Show all
              </Button>
            </Flex>

            {events.length === 0 ? (
              <Text color={theme.neutral5}>
                Nothing scheduled on {formattedDate}
              </Text>
            ) : (
              <Flex flexDirection="column" gap="0.75em">
                {events.map((event) => (
                  <EventRow
                    date={activeDateForMonth}
                    event={event}
                    experience={experience}
                    key={event.id}
                    theme={theme}
                  />
                ))}
              </Flex>
            )}
          </FadeInOut>
        </AnimatePresence>
      </Box>
    );
  }

  const daysWithEvents = monthData.boxes.filter(
    (b) => isInMonth(b.date) && b.events.length > 0
  );
  const monthLabel = monthStart.toFormat('LLLL yyyy');

  return (
    <Box
      borderTopColor={theme.accent2Color}
      borderTopStyle="solid"
      borderTopWidth="1px"
      mt="1.5em"
      pt="1em"
      px="0.5em"
    >
      <AnimatePresence mode="wait">
        <FadeInOut direction="column" key="all-events">
          <Text
            color={theme.textColor}
            fontSize="1.1em"
            fontWeight="600"
            mb="0.5em"
          >
            {monthLabel}
          </Text>

          {daysWithEvents.length === 0 ? (
            <Text color={theme.neutral5}>Nothing scheduled this month</Text>
          ) : (
            <Flex flexDirection="column" gap="1em">
              {daysWithEvents.map((box) => (
                <Flex
                  flexDirection="column"
                  gap="0.4em"
                  key={box.date.toISODate()}
                >
                  <Text
                    color={theme.neutral3 || theme.textColor}
                    fontSize="1.05em"
                    fontWeight="600"
                  >
                    {box.date.toFormat('ccc, LLL d')}
                  </Text>
                  <Flex flexDirection="column" gap="0.5em" pl="0.25em">
                    {box.events.map((event) => (
                      <EventRow
                        date={box.date}
                        event={event}
                        experience={experience}
                        key={event.id}
                        theme={theme}
                      />
                    ))}
                  </Flex>
                </Flex>
              ))}
            </Flex>
          )}
        </FadeInOut>
      </AnimatePresence>
    </Box>
  );
};
